import React, { useEffect, useState } from "react";
import { Server, Copy, Check } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import { RowLineSkeleton } from "../../components/Skeleton/StatsSkeleton.jsx";

/* ===================== STYLES ===================== */
import styles from "./ApiDetailsCard.module.scss";

/**
 * ApiDetailsCard
 * --------------
 * Admin View  → Pre + Prod + Client URLs
 * Client View → Only Client URL
 */
const ApiDetailsCard = ({ apiData, isAdminView = false }) => {
  const [copiedField, setCopiedField] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setLoading(false);
    // const timer = setTimeout(() => { ... }, 1200);
    return () => clearTimeout();
  }, []);

  const handleCopy = (text, field) => {
    if (!text || text === "-") return;

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text);
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
    }

    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  return (
    <div className={styles["admin-ac-api-details-card"]}>
      {/* HEADER */}
      <div className={styles["admin-ac-api-details-card__header"]}>
        <div className={styles["admin-ac-api-details-card__icon-wrapper"]}>
          <Server size={18} strokeWidth={2.5} />
        </div>
        <h3 className={styles["admin-ac-api-details-card__title"]}>
          API Details
        </h3>
      </div>

      <div className={styles["admin-ac-api-details-card__body"]}>
        {/* ================= ADMIN VIEW ================= */}
        {isAdminView && (
          <>
            {/* Pre-Production URL */}
            <div className={styles["admin-ac-api-details-card__field"]}>
              <label className={styles["admin-ac-api-details-card__label"]}>
                Pre-Production URL
              </label>

              <div
                className={styles["admin-ac-api-details-card__input-wrapper"]}
              >
                <input
                  type="text"
                  className={styles["admin-ac-api-details-card__input"]}
                  value={apiData?.pre_production_url || "-"}
                  readOnly
                />

                <button
                  type="button"
                  className={`${styles["admin-ac-api-details-card__copy-btn"]} ${
                    copiedField === "pre"
                      ? styles["admin-ac-api-details-card__copy-btn--success"]
                      : ""
                  }`}
                  onClick={() => handleCopy(apiData?.pre_production_url, "pre")}
                  title="Copy URL"
                >
                  {copiedField === "pre" ? (
                    <Check size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>

                {copiedField === "pre" && (
                  <span
                    className={
                      styles["admin-ac-api-details-card__copy-feedback"]
                    }
                  >
                    Copied!
                  </span>
                )}
              </div>
            </div>

            {/* Production URL */}
            <div className={styles["admin-ac-api-details-card__field"]}>
              <label className={styles["admin-ac-api-details-card__label"]}>
                Production URL
              </label>

              <div
                className={styles["admin-ac-api-details-card__input-wrapper"]}
              >
                <input
                  type="text"
                  className={styles["admin-ac-api-details-card__input"]}
                  value={apiData?.production_url || "-"}
                  readOnly
                />

                <button
                  type="button"
                  className={`${styles["admin-ac-api-details-card__copy-btn"]} ${
                    copiedField === "prod"
                      ? styles["admin-ac-api-details-card__copy-btn--success"]
                      : ""
                  }`}
                  onClick={() => handleCopy(apiData?.production_url, "prod")}
                  title="Copy URL"
                >
                  {copiedField === "prod" ? (
                    <Check size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>

                {copiedField === "prod" && (
                  <span
                    className={
                      styles["admin-ac-api-details-card__copy-feedback"]
                    }
                  >
                    Copied!
                  </span>
                )}
              </div>
            </div>
          </>
        )}

        {/* ================= CLIENT URL (ADMIN + CLIENT) ================= */}
        <div className={styles["admin-ac-api-details-card__field"]}>
          <label className={styles["admin-ac-api-details-card__label"]}>
            Method
          </label>
          <span className={styles["admin-ac-api-details-card__method-tag"]}>
            {apiData?.methods || "POST"}
          </span>
        </div>

        <div className={styles["admin-ac-api-details-card__field"]}>
          <label className={styles["admin-ac-api-details-card__label"]}>
            Client URL
          </label>

          <div className={styles["admin-ac-api-details-card__input-wrapper"]}>
            {loading ? (
              <RowLineSkeleton />
            ) : (
              <>
                <input
                  type="text"
                  className={styles["admin-ac-api-details-card__input"]}
                  value={apiData?.client_url || "-"}
                  readOnly
                />

                <button
                  type="button"
                  className={`${styles["admin-ac-api-details-card__copy-btn"]} ${
                    copiedField === "client"
                      ? styles["admin-ac-api-details-card__copy-btn--success"]
                      : ""
                  }`}
                  onClick={() => handleCopy(apiData?.client_url, "client")}
                  title="Copy URL"
                >
                  {copiedField === "client" ? (
                    <Check size={16} />
                  ) : (
                    <Copy size={16} />
                  )}
                </button>

                {copiedField === "client" && (
                  <span
                    className={
                      styles["admin-ac-api-details-card__copy-feedback"]
                    }
                  >
                    Copied!
                  </span>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiDetailsCard;
